import { Injectable } from 'angular-ts-decorators';

export type LazyLoadCountFunction = () => ng.IPromise<any>;
export type LazyLoadLoadFunction = (page: number) => ng.IPromise<any>;

/**
 * LazyLoad class for managing the lazy load requests.
 */
// TODO rename to LazyLoadObject
export class LazyLoad {
  private _countFunction: LazyLoadCountFunction;
  private _loadFunction: LazyLoadLoadFunction;
  private _page: number;
  private _count: number;
  private _loading: boolean;
  private _full: boolean;
  private _items: any[];

  constructor(
    countFunction: LazyLoadCountFunction,
    loadFunction: LazyLoadLoadFunction,
  ) {
    this._page = 0;
    this._count = 0;
    this._loading = false;
    this._full = false;
    this._items = [];

    this._countFunction = countFunction;
    this._loadFunction = loadFunction;
  }

  /**
   *
   * Will load new data into the items array based on given filters
   * @return {Void}
   * Nothing
   */
  load(): ng.IPromise<any> {
    if (!this.loading) {
      this._loading = true;

      return this._loadFunction(this.page).then((result) => {
        this._items = this.items.concat(result);
        this._loading = false;
        this._page += 1;

        if (this.count <= this.items.length) {
          this._full = true;
        }
      });
    }
  }

  /**
   *
   * Calls lazyLoad basically loading the first dataset based on the filters and fields
   * @return {Void}
   * Nothing
   */
  init(): ng.IPromise<number> {
    return this._countFunction().then((result) => {
      this._count = result;

      return this.load();
    });
  }

  get page() {
    return this._page;
  }
  get count() {
    return this._count;
  }
  get loading() {
    return this._loading;
  }
  get full() {
    return this._full;
  }
  get items() {
    return this._items;
  }
}

/**
 * lazyLoadFactory service provides methods to create {@link
 * coreModule.LazyLoad} compatible objects
 */
// TODO rename to LazyLoad
@Injectable('lazyLoadFactory')
export class LazyLoadFactory {
  constructor(private $q: ng.IQService) {
    'ngInject';
  }

  /**
   * create lazy load object that uses provided callbacks
   * @param {Function} countFunction method that returns total of available
   * elements
   * @param {Function} loadFunction method that returns next bunch of values
   * @return { LazyLoad } created object
   */
  create(
    countFunction: LazyLoadCountFunction,
    loadFunction: LazyLoadLoadFunction,
  ): LazyLoad {
    return new LazyLoad(countFunction, loadFunction);
  }

  /**
   * create lazy load object that provided array, as in-memory source for lazy
   * load
   * @param {any[]} data data to be paginated
   * @param {number} pageSize optional parameter to set the page size
   * @return { LazyLoad } created object
   */
  fromArray(data: any[], pageSize: number = 10): LazyLoad {
    if (!angular.isArray(data)) {
      return new LazyLoad(
        () =>
          this.$q.reject(
            `fromArray has to be called with an array, instead was called with ${data}`,
          ),
        () => this.$q.reject(),
      );
    }

    return new LazyLoad(
      () => this.$q.when(data.length),
      (page) =>
        this.$q.when(data.slice(page * pageSize, (page + 1) * pageSize)),
    );
  }
}
